import { env } from "../config/env";
import { parseContentDispositionFilename } from "../lib/http/downloadBlob";
import { defaultDownloadFilename } from "../lib/forms/submissionsExport";
import { notifyUnauthorized } from "./unauthorized";

export type ApiErrorBody = {
  message?: string;
  error?: string;
  errors?: Record<string, string[]>;
};

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly errors?: Record<string, string[]>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Явный legacy Bearer для локальной разработки. SSO работает через cookie. */
export function getAccessToken(): string | null {
  return env.apiToken || null;
}

let formUserId: string | null = null;

/** CHM ID из центрального профиля для авторизации конструктора форм. */
export function setFormUserId(value: string | number | null | undefined): void {
  const next = value == null ? "" : String(value).trim();
  formUserId = next || null;
}

async function readJsonBody(response: Response): Promise<unknown | null> {
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    return null;
  }

  try {
    return await response.json();
  } catch {
    return null;
  }
}

function messageFromErrorBody(
  body: unknown,
  fallback: string,
): { message: string; errors?: Record<string, string[]> } {
  if (!body || typeof body !== "object") {
    return { message: fallback };
  }

  const parsed = body as ApiErrorBody;
  const errors = parsed.errors;
  const fromMessage =
    typeof parsed.message === "string" && parsed.message.trim() !== ""
      ? parsed.message.trim()
      : null;
  const fromError =
    typeof parsed.error === "string" && parsed.error.trim() !== ""
      ? parsed.error.trim()
      : null;
  const message = fromMessage ?? fromError ?? fallback;

  return { message, errors };
}

type RequestOptions = RequestInit & {
  baseUrl?: string;
  auth?: boolean;
  /** Не вызывать глобальный logout/redirect при 401 (login/logout). */
  skipUnauthorizedHandler?: boolean;
  /** Явный Bearer (share accessToken и т.п.), вместо токена админки. */
  bearerToken?: string | null;
};

export type ApiDownloadResult = {
  blob: Blob;
  filename: string;
};

function authHeaders(auth: boolean, bearerToken?: string | null): HeadersInit {
  const explicitBearer = bearerToken?.trim();
  if (explicitBearer) {
    return { Authorization: `Bearer ${explicitBearer}` };
  }
  if (!auth) return {};
  if (formUserId) return { "X-User-Id": formUserId };

  const legacyToken = getAccessToken();
  return legacyToken ? { Authorization: `Bearer ${legacyToken}` } : {};
}

function handleUnauthorizedResponse(
  status: number,
  auth: boolean,
  skipUnauthorizedHandler: boolean,
): void {
  if (status === 401 && auth && !skipUnauthorizedHandler) {
    notifyUnauthorized();
  }
}

export async function apiDownload(
  path: string,
  options: RequestOptions = {},
): Promise<ApiDownloadResult> {
  const {
    baseUrl = env.apiBaseUrl,
    auth = true,
    skipUnauthorizedHandler = false,
    bearerToken,
    headers,
    ...init
  } = options;

  const response = await fetch(`${baseUrl}${path}`, {
    ...init,
    credentials: init.credentials ?? "include",
    headers: {
      Accept: "*/*",
      "X-Requested-With": "XMLHttpRequest",
      ...authHeaders(auth, bearerToken),
      ...headers,
    },
  });

  if (!response.ok) {
    handleUnauthorizedResponse(response.status, auth, skipUnauthorizedHandler);
    const body = await readJsonBody(response);
    const { message, errors } = messageFromErrorBody(
      body,
      `Request failed: ${response.status} ${response.statusText}`,
    );
    throw new ApiError(message, response.status, errors);
  }

  const blob = await response.blob();
  const contentType = response.headers.get("content-type") ?? "";
  const defaultName = defaultDownloadFilename(contentType);
  const filename =
    parseContentDispositionFilename(
      response.headers.get("Content-Disposition"),
    ) ?? defaultName;

  return { blob, filename };
}

export async function apiRequest<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const {
    baseUrl = env.apiBaseUrl,
    auth = true,
    skipUnauthorizedHandler = false,
    bearerToken,
    headers,
    ...init
  } = options;

  const response = await fetch(`${baseUrl}${path}`, {
    ...init,
    credentials: init.credentials ?? "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      ...authHeaders(auth, bearerToken),
      ...headers,
    },
  });

  if (!response.ok) {
    handleUnauthorizedResponse(response.status, auth, skipUnauthorizedHandler);
    const body = await readJsonBody(response);
    const { message, errors } = messageFromErrorBody(
      body,
      `Request failed: ${response.status} ${response.statusText}`,
    );
    throw new ApiError(message, response.status, errors);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const body = await readJsonBody(response);
  return body as T;
}
